import { useMemo, useState } from 'react';
import { Open } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { openFilePickerAndUploadFiles } from '../../FileUploader/FileUploaderUtility';
import { HelpPopupContent } from '../../HelpPopup/__internal__/';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { isCustomSection } from '../../Menu/__internal__';
import { useObjectMarkerLocalizer } from '../../ObjectMarker';
import { getIconAndLabelTextFromType } from '../../ObjectMarker/__internal__/getIconAndLabelTextFromType';
import { PopperContent } from '../../ObjectMarker/__internal__/PopperContent';
import { getSegmentContent, isSegmentDisabled } from '../../SegmentedButton/__internal__';
import { getUndoRedoProps, useActionManagers } from '../../UndoRedo/__internal__/useActionManager';
import { renderItem } from './';
import { getIconText } from './PredefinedButton';
import bundle from '../NovaComponents-Toolbar-gui-icu.properties';
import { jsx as _jsx } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const getOveflowedItems = (overflowPriorities, prevOverflowedItems, availableWidth, itemContainers, responsiveSets) => {
  if (availableWidth === 0) return prevOverflowedItems;
  const prevOverflowedIndices = prevOverflowedItems.map(({
    index
  }) => index);
  if (availableWidth < 0) {
    // When the width is too narrow to hold currently non-overflowed items
    // Overflow the next-to-overflow items
    let overflowedWidth = -availableWidth;
    const toOverflowItems = [];
    do {
      const nextOverflowIndex = getNextOverflowIndex(overflowPriorities, [...prevOverflowedIndices, ...toOverflowItems.map(({
        index
      }) => index)]);
      if (nextOverflowIndex < 0) break;
      const nextOverflowIndices = responsiveSets.find(indices => indices.includes(nextOverflowIndex)) ?? [nextOverflowIndex];
      const prevToOverflowItemsCount = toOverflowItems.length;
      for (const nextOverflowIndex of nextOverflowIndices) {
        if (overflowPriorities[nextOverflowIndex] === 'never') continue;
        const nextOverflowElement = itemContainers[nextOverflowIndex];
        if (!nextOverflowElement) continue;
        const itemWidth = getContainerWidth(nextOverflowElement);
        toOverflowItems.push({
          index: nextOverflowIndex,
          width: itemWidth
        });
        overflowedWidth -= itemWidth;
      }
      // If there is no item added to the toOverflowItems, break out of the loop
      // This could happen when itemContainers has not been updated yet
      if (toOverflowItems.length === prevToOverflowItemsCount) break;
    } while (overflowedWidth > 0);
    if (toOverflowItems.length > 0) {
      return [...prevOverflowedItems, ...toOverflowItems];
    }
  } else {
    // When there is available space, check if some overflowed items no longer need to be overflowed

    // When an item is unoverflowed, it can cause the item after it to have a toolbar separtor and/or margin
    // to show up, so minus 20 to make sure the available width is big enough even when that happens
    if (overflowPriorities.length !== 1) {
      availableWidth -= 20;
    }
    const toRestoreIndices = [];
    do {
      const nextRestoreIndex = getNextRestoreIndex(overflowPriorities, [...prevOverflowedIndices.filter(index => !toRestoreIndices.includes(index))]);
      if (nextRestoreIndex < 0) break;
      const nextRestoreIndices = responsiveSets.find(indices => indices.includes(nextRestoreIndex)) ?? [nextRestoreIndex];
      const nextRestoreItems = prevOverflowedItems.filter(({
        index
      }) => nextRestoreIndices.includes(index));
      const totalWidth = nextRestoreItems.reduce((prevWidth, {
        width
      }) => prevWidth + width, 0);
      if (nextRestoreItems.length === 0 || availableWidth < totalWidth) {
        break;
      }
      toRestoreIndices.push(...nextRestoreIndices);
      availableWidth -= totalWidth;
    } while (availableWidth > 0);
    if (toRestoreIndices.length > 0) {
      return prevOverflowedItems.filter(({
        index
      }) => !toRestoreIndices.includes(index));
    }
  }
  return prevOverflowedItems;
};
export const getOverflowConfig = ({
  item,
  index,
  localize,
  objectMarkerLocalize,
  getIconA11yInfo,
  responsiveSetsConfig
}) => {
  // Not all items can be cast to ComponentOverflowConfig but in those cases where they cannot,
  // `responsiveSetId` resolving to `undefined` is also acceptable
  const {
    responsiveSetId
  } = item;
  const overflowPriority = responsiveSetId && responsiveSetsConfig ?
  // Responsive set's overflowPriority overrides item's overflowPriority
  responsiveSetsConfig[responsiveSetId].overflowPriority : getOverflowPriority(item);
  switch (item.type) {
    case 'Button': // falls-through
    case 'PredefinedButton':
      {
        const {
          icon,
          text
        } = item.type === 'Button' ? {
          icon: item.icon,
          text: item.text
        } : getIconText(item.buttonType, false, localize); // iconOnly is set to false when overflowed
        const iconA11yInfo = icon ? getIconA11yInfo(icon) : {};
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? text ?? item.tooltip ?? iconA11yInfo.tooltip ?? localize('Button.asMenuItem.label'),
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: () => item.onPress?.()
        };
      }
    case 'BadgeButton':
      {
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? item.tooltip ?? item.value.toString(),
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: () => item.onPress?.()
        };
      }
    case 'BaseMenuButton':
      // If there is CustomSection, never overflow it, because CustomSection can't be as level 2 menu item.
      if (item.sections.some(isCustomSection)) {
        return {
          overflowPriority,
          responsiveSetId
        };
      }
    // falls-through
    case 'MenuButton':
      const iconA11yInfo = item.icon ? getIconA11yInfo(item.icon) : {};
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? item.text ?? item.tooltip ?? iconA11yInfo.tooltip ?? localize('MenuButton.asMenuItem.label'),
          subSections: item.sections,
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: (indices, ...restArgs) => item.onItemSelected?.(indices.slice(1), ...restArgs),
        onItemClosed: indices => item.onItemClosed?.(indices.slice(1))
      };
    case 'Select': // falls-through
    case 'SelectWithLabels':
      {
        const defaultLabel = (item.type === 'SelectWithLabels' ? item.label : undefined) ?? localize('Dropdown.asMenuItem.label');
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? defaultLabel,
            subSections: item.values ? [{
              items: item.values.reduce((prevItems, {
                text
              }, index) => text ? [...prevItems, {
                text,
                checked: item.selectedIndex === index
              }] : prevItems, [])
            }] : [],
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: indices => {
            if (indices.length < 2) return; // Early return if the level 0 item is selected
            const selectingIndex = indices[1][1]; // indices[1] has format of [sectionIndex, itemIndex]
            if (item.selectedIndex === selectingIndex) {
              // When there is unselectedValue, selecting the currently selected item deselects it
              if (item.unselectedValue) {
                item.onSelected?.(undefined);
              }
            } else {
              item.onSelected?.(selectingIndex);
            }
          }
        };
      }
    case 'SegmentedButton':
      return {
        overflowPriority,
        responsiveSetId,
        // Each button in SegmentedButton overflows as a single menu item
        menuItems: item.values.map((value, index) => {
          const segmentContent = getSegmentContent(value);
          const defaultLabel = typeof segmentContent === 'string' ? segmentContent : getIconA11yInfo(segmentContent).tooltip ?? '';
          return {
            text: item.menuItemText?.[index] ?? defaultLabel,
            icon: typeof segmentContent === 'string' ? undefined : segmentContent.icon,
            checked: item.selectedIndex === index,
            disabled: isSegmentDisabled(value) || item.disabled,
            testId: postfixTestId(item.testId, `button-${index}`)
          };
        }),
        onSelected: indices => {
          item.onSelectedIndex(indices[0][1]);
        }
      };
    case 'SplitButton':
      return {
        overflowPriority,
        responsiveSetId,
        // Each button in SegmentedButton overflows as a single menu item
        menuItems: item.items.map((splitButtonItem, index) => {
          const defaultLabel = typeof splitButtonItem === 'string' ? splitButtonItem : splitButtonItem.text;
          return {
            text: item.menuItemText?.[index] ?? defaultLabel,
            disabled: item.disabled,
            testId: postfixTestId(item.testId, `item-${index}`)
          };
        }),
        onSelected: indices => {
          item.onItemSelected(indices[0][1]);
        }
      };
    case 'SearchField': // falls-through
    case 'SearchFieldWithLabels':
      {
        const defaultLabel = (item.type === 'SearchFieldWithLabels' ? item.label : undefined) ?? (item.filterMode ? localize('SearchField.filterMode.asMenuItem.label') : localize('SearchField.asMenuItem.label'));
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? defaultLabel,
            disabled: item.disabled,
            testId: item.testId
          }],
          // FIXME: get label from item's props
          onSelected: () => {
            return {
              index,
              title: item.filterMode ? localize('SearchField.filterMode.asDialog.title') : localize('SearchField.asDialog.title'),
              renderContent: item => {
                if (item.type !== 'SearchField' && item.type !== 'SearchFieldWithLabels') return;
                return renderItem(item);
              }
            };
          }
        };
      }
    case 'HelpPopup':
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? localize('HelpPopup.asMenuItem.label'),
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: () => {
          return {
            index,
            title: localize('HelpPopup.asDialog.title'),
            renderContent: item => {
              if (item.type !== 'HelpPopup') return;
              const {
                overflowPriority,
                menuItemText,
                ...rest
              } = item;
              return /*#__PURE__*/_jsx(HelpPopupContent, {
                ...rest
              });
            }
          };
        }
      };
    case 'Input': // falls-through
    case 'InputWithLabels':
      {
        const defaultLabel = (item.type === 'InputWithLabels' ? item.label : undefined) ?? localize('Input.asMenuItem.label');
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? defaultLabel,
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: () => {
            return {
              index,
              title: localize('Input.asDialog.title'),
              renderContent: item => {
                if (item.type !== 'Input' && item.type !== 'InputWithLabels') return;
                return renderItem(item);
              }
            };
          }
        };
      }
    case 'ComboBox': // falls-through
    case 'ComboBoxWithLabels':
      {
        const defaultLabel = (item.type === 'ComboBoxWithLabels' ? item.label : undefined) ?? localize('ComboBox.asMenuItem.label');
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? defaultLabel,
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: () => {
            return {
              index,
              title: localize('ComboBox.asDialog.title'),
              renderContent: item => {
                if (item.type !== 'ComboBox' && item.type !== 'ComboBoxWithLabels') return;
                return renderItem(item);
              }
            };
          }
        };
      }
    case 'Switch':
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? localize('Switch.asMenuItem.label'),
          checked: item.isOn,
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: () => {
          item.onPress?.(!item.isOn);
          return {
            index,
            title: localize('Switch.asDialog.title'),
            renderContent: item => {
              if (item.type !== 'Switch' && item.type !== 'SwitchWithLabels') return;
              return renderItem(item);
            }
          };
        }
      };
    case 'SwitchWithLabels':
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? item.label,
          checked: item.isOn,
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: () => {
          item.onPress?.(!item.isOn);
        }
      };
    case 'Checkbox':
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? item.label ?? localize('Checkbox.asMenuItem.label'),
          checked: item.checked,
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: item.onPress
      };
    case 'ToggleButton':
      {
        const iconA11yInfo = item.icon ? getIconA11yInfo(item.icon) : {};
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? item.text ?? iconA11yInfo.tooltip ?? localize('ToggleButton.asMenuItem.label'),
            checked: item.pressed ?? false,
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: item.onPress ? () => {
            item.onPress?.();
          } : undefined
        };
      }
    case 'ObjectMarker':
      const label = item.text ?? getIconAndLabelTextFromType(item.objectMarkerType, objectMarkerLocalize).labelText;
      return {
        overflowPriority,
        responsiveSetId,
        menuItems: [{
          text: item.menuItemText ?? label,
          disabled: item.disabled,
          testId: item.testId
        }],
        onSelected: () => {
          return {
            index,
            title: label,
            renderContent: item => {
              if (item.type !== 'ObjectMarker') return;
              return /*#__PURE__*/_jsx(PopperContent, {
                content: item.popperText ?? ''
              });
            }
          };
        }
      };
    case 'UndoRedo':
      {
        const {
          undoProps,
          redoProps
        } = getUndoRedoProps(item.actionManager);
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText?.[0] ?? localize('UndoRedo.undo.asMenuItem.label'),
            disabled: undoProps.disabled,
            subSections: undoProps.disabled ? undefined : undoProps.sections,
            testId: postfixTestId(item.testId, 'undoButton')
          }, {
            text: item.menuItemText?.[1] ?? localize('UndoRedo.redo.asMenuItem.label'),
            disabled: redoProps.disabled,
            subSections: redoProps.disabled ? undefined : redoProps.sections,
            testId: postfixTestId(item.testId, 'redoButton')
          }],
          onSelected: (indices, ...restArgs) => {
            const isUndoSelected = indices[0][1] === 0;
            if (isUndoSelected) {
              undoProps.onItemSelected(indices.slice(1), ...restArgs);
            } else {
              redoProps.onItemSelected(indices.slice(1), ...restArgs);
            }
          }
        };
      }
    case 'FileUploader':
      {
        // Only button-only FileUploader can overflow
        if (!item.buttonOnly) {
          return {
            overflowPriority,
            responsiveSetId
          };
        }
        const iconA11yInfo = getIconA11yInfo({
          icon: item.icon ?? Open
        });
        return {
          overflowPriority,
          responsiveSetId,
          menuItems: [{
            text: item.menuItemText ?? item.buttonText ?? iconA11yInfo.tooltip ?? '',
            disabled: item.disabled,
            testId: item.testId
          }],
          onSelected: () => {
            openFilePickerAndUploadFiles(item);
          }
        };
      }
    case 'ToolbarSeparator': // falls-through
    case 'ToolbarSpacer':
      return {
        overflowPriority,
        responsiveSetId
      };
    default:
      return {
        overflowPriority,
        responsiveSetId
      };
  }
};
export const useOverflowConfigs = (items, responsiveSetsConfig) => {
  const localize = useTextLocalizer();
  const objectMarkerLocalize = useObjectMarkerLocalizer();
  const getIconA11yInfo = useIconA11yInfo();
  const [forceUpdateCount, setForceUpdateCount] = useState(0);
  const overflowConfigs = useMemo(() => items.map((item, index) => getOverflowConfig({
    item,
    index,
    localize,
    objectMarkerLocalize,
    getIconA11yInfo,
    responsiveSetsConfig
  })),
  // Include `forceUpdateCount` on purpose for force updating overflowConfigs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [getIconA11yInfo, items, localize, objectMarkerLocalize, responsiveSetsConfig, forceUpdateCount]);
  const actionManagers = useMemo(() => {
    const actionManagers = [];
    for (const item of items) {
      if (item.type !== 'UndoRedo') {
        continue;
      }
      actionManagers.push(item.actionManager);
    }
    return actionManagers;
  }, [items]);

  // When Toolbar contains any UndoRedo, set listeners to the actionManager(s) so that the sections of the overflow menu button
  // would be updated when actionHistoryChanged event is fired
  useActionManagers(actionManagers, () => {
    setForceUpdateCount(prev => prev + 1);
  });
  return overflowConfigs;
};
export const getAvailableWidth = (items, toolbar, itemContainers, extraSpacerRefs, ignoreOverflowButton) => {
  const clientWidth = toolbar?.clientWidth ?? 0;
  if (clientWidth === 0) return 0;
  const contentWidth = getToolbarContentWidth(items, itemContainers, ignoreOverflowButton);
  let extraAvailableWidth = 0;
  for (const extraSpacerRef of extraSpacerRefs ?? []) {
    if (!extraSpacerRef.current) continue;
    const spacerWidth = extraSpacerRef.current?.clientWidth ?? 0;
    extraAvailableWidth += spacerWidth;
    const spacerMinWidth = parseInt(getComputedStyle(extraSpacerRef.current).minWidth);
    if (spacerMinWidth > 0) {
      extraAvailableWidth -= spacerMinWidth;
    }
  }
  return clientWidth - contentWidth + extraAvailableWidth;
};
export const getToolbarContentWidth = (items, itemContainers, ignoreOverflowButton) => {
  let contentWidth = 0;
  for (const [index, itemContainer] of itemContainers.entries()) {
    if (items[index]?.type === 'ToolbarSpacer') continue;
    // if itemContainer is overflowButton, its index is items.length.
    // if there is only one item, need to avoid reducing the available width due to the overflowButton
    if (ignoreOverflowButton && !items[index]) continue;
    contentWidth += itemContainer ? getContainerMinWidth(itemContainer) : 0;
  }
  return Math.ceil(contentWidth);
};
const getContainerWidth = element => {
  const {
    marginLeft,
    marginRight
  } = getComputedStyle(element);
  // Do not use element.getBoundingClientRect().width because it doesn't work with Dialog's tranformation
  return element.offsetWidth + parseFloat(marginLeft) + parseFloat(marginRight);
};
const getContainerMinWidth = element => {
  const elementStyles = getComputedStyle(element);
  const minWidth = parseInt(elementStyles.minWidth);
  if (!minWidth) return getContainerWidth(element);
  const oldWidth = element.style.width;
  const oldFlexBasis = element.style.flexBasis;
  element.style.width = '0px';
  element.style.flexBasis = '';
  let childrenMinWidth = 0;
  for (const child of element.children) {
    childrenMinWidth += getContainerWidth(child);
  }
  element.style.width = oldWidth;
  element.style.flexBasis = oldFlexBasis;
  return Math.max(minWidth, childrenMinWidth) + parseFloat(elementStyles.marginLeft) + parseFloat(elementStyles.marginRight);
};
export const getNextOverflowIndex = (overflowPriorities, overflowedIndices) => {
  // Overflow order: from right to left and 'disappear' > 'low' > 'high'
  let largestHighPriorityIndex = -1;
  let largestLowPriorityIndex = -1;
  let largestDisappearPriorityIndex = -1;
  for (const [index, overflowPriority] of overflowPriorities.entries()) {
    if (overflowedIndices.includes(index)) continue;
    switch (overflowPriority) {
      case 'low':
        largestLowPriorityIndex = largestLowPriorityIndex < 0 ? index : Math.max(largestLowPriorityIndex, index);
        break;
      case 'high':
        largestHighPriorityIndex = largestHighPriorityIndex < 0 ? index : Math.max(largestHighPriorityIndex, index);
        break;
      case 'disappear':
        largestDisappearPriorityIndex = largestDisappearPriorityIndex < 0 ? index : Math.max(largestDisappearPriorityIndex, index);
        break;
      case 'never':
      case 'always':
        break;
    }
  }
  return largestDisappearPriorityIndex >= 0 ? largestDisappearPriorityIndex : largestLowPriorityIndex >= 0 ? largestLowPriorityIndex : largestHighPriorityIndex;
};
export const getNextRestoreIndex = (overflowPriorities, overflowedIndices) => {
  // Restore order: from left to right and 'high' > 'low' > 'disappear'
  let smallestLowPriorityIndex = -1;
  let smallestHighPriorityIndex = -1;
  let smallestDisappearPriorityIndex = -1;
  for (const overflowedIndex of overflowedIndices) {
    switch (overflowPriorities[overflowedIndex]) {
      case 'low':
        smallestLowPriorityIndex = smallestLowPriorityIndex < 0 ? overflowedIndex : Math.min(smallestLowPriorityIndex, overflowedIndex);
        break;
      case 'high':
        smallestHighPriorityIndex = smallestHighPriorityIndex < 0 ? overflowedIndex : Math.min(smallestHighPriorityIndex, overflowedIndex);
        break;
      case 'disappear':
        smallestDisappearPriorityIndex = smallestDisappearPriorityIndex < 0 ? overflowedIndex : Math.min(smallestDisappearPriorityIndex, overflowedIndex);
        break;
      case 'never': // falls-through
      case 'always':
        break;
    }
  }
  return smallestHighPriorityIndex >= 0 ? smallestHighPriorityIndex : smallestLowPriorityIndex >= 0 ? smallestLowPriorityIndex : smallestDisappearPriorityIndex;
};
export const getOverflowPriority = item => {
  switch (item.type) {
    case 'BadgeButton': // falls-through
    case 'Button':
      if ('customTooltipProps' in item) {
        return item.overflowPriority ?? 'never';
      }
    case 'PredefinedButton': // falls-through
    case 'MenuButton': // falls-through
    case 'Select': // falls-through
    case 'SelectWithLabels': // falls-through
    case 'Switch': // falls-through
    case 'SwitchWithLabels': // falls-through
    case 'SegmentedButton': // falls-through
    case 'Checkbox': // falls-through
    case 'UndoRedo': // falls-through
    case 'ToggleButton':
      return item.overflowPriority ?? 'low';
    case 'BaseMenuButton':
      // If there is CustomSection, never overflow it, because CustomSection can't be as level 2 menu item.
      if (item.sections.some(isCustomSection)) {
        return 'never';
      } else {
        return item.overflowPriority ?? 'low';
      }
    case 'FileUploader':
      if (item.buttonOnly) return item.overflowPriority ?? 'low';
      return 'never';
    case 'ObjectMarker':
      // Only the active objectMarkers are allowed to be overflowed
      return item.popperText ? item.overflowPriority ?? 'never' : 'never';
    case 'SearchField': // falls-through
    case 'SearchFieldWithLabels': // falls-through
    case 'HelpPopup': // falls-through
    case 'SplitButton': // falls-through
    case 'Input': // falls-through
    case 'InputWithLabels': // falls-through
    case 'ComboBox': // falls-through
    case 'ComboBoxWithLabels':
      return item.overflowPriority ?? 'never';
    case 'DatePicker': // falls-through
    case 'DatePickerWithLabels': // falls-through
    case 'ToolbarSeparator': // falls-through
    case 'ToolbarSpacer': // falls-through
    case 'Heading': // falls-through
    case 'Text': // falls-through
    case 'FileUploaderWithLabels': // falls-through
    case 'Breadcrumb':
      return 'never';
    default:
      return 'never';
  }
};
export const getAlwaysOverflowItems = overflowPriorities => overflowPriorities.reduce((prev, priority, index) => priority === 'always' ? [...prev, index] : prev, []).map(itemIndex => ({
  index: itemIndex,
  width: 0
}));
export const getMinimalStateItems = ({
  items,
  overflowed,
  prevMinimalStateItems,
  availableWidth,
  itemContainers,
  responsiveSets
}) => {
  if (availableWidth <= 0) {
    const responsiveIndices = getResponsiveIndices(items, overflowed, prevMinimalStateItems);
    if (responsiveIndices.length === 0) return prevMinimalStateItems;
    const nextRespondIndex = responsiveIndices[responsiveIndices.length - 1];
    const indicesOfTheSameGroup = responsiveSets.find(indices => indices.includes(nextRespondIndex)) ?? [nextRespondIndex];
    const nextRespondIndices = indicesOfTheSameGroup.filter(nextRespondIndex => responsiveIndices.includes(nextRespondIndex) &&
    // the item is responsive
    !prevMinimalStateItems.find(({
      index
    }) => index === nextRespondIndex) // the item is not minimal state yet
    );

    return [...prevMinimalStateItems, ...nextRespondIndices.map(nextRespondIndex => ({
      index: nextRespondIndex,
      originalWidth: itemContainers[nextRespondIndex]?.clientWidth ?? 0
    }))];
  } else {
    prevMinimalStateItems.sort(({
      index: index1
    }, {
      index: index2
    }) => index1 - index2); // Sort by the index in ascending order
    const toRestoreIndices = [];
    for (const item of prevMinimalStateItems) {
      if (toRestoreIndices.includes(item.index)) continue;
      const nextRestoreIndices = responsiveSets.find(indices => indices.includes(item.index)) ?? [item.index];
      const nextRestoreItems = prevMinimalStateItems.filter(item => nextRestoreIndices.includes(item.index));
      const totalDeltaWidth = nextRestoreItems.reduce((prevDeltaWidth, item) => {
        const itemCurrentWidth = itemContainers[item.index]?.clientWidth ?? 0;
        const deltaWidth = item.originalWidth - itemCurrentWidth;
        return prevDeltaWidth + deltaWidth;
      }, 0);
      if (availableWidth <= totalDeltaWidth) break;
      availableWidth -= totalDeltaWidth;
      toRestoreIndices.push(...nextRestoreIndices);
    }
    return toRestoreIndices.length === 0 ? prevMinimalStateItems : prevMinimalStateItems.filter(({
      index
    }) => !toRestoreIndices.includes(index));
  }
};
export const getResponsiveIndices = (items, overflowed, minimalStateItems) => {
  const overflowedIndices = overflowed.map(({
    index
  }) => index);
  const minimalStateIndices = minimalStateItems.map(({
    index
  }) => index);
  return items.reduce((prev, item, index) => !overflowedIndices.includes(index) && !minimalStateIndices.includes(index) && isResponsive(item) ? [...prev, index] : prev, []);
};
const isResponsive = item => {
  switch (item.type) {
    case 'PredefinedButton':
      return item.type === 'PredefinedButton' && item.iconOnly === undefined;
    case 'Button': // falls-through
    case 'ToggleButton':
      return item.text !== undefined && item.text.length > 0 && item.icon !== undefined;
    case 'FileUploader':
      return (
        // FileUploader is only responsive when it is button-only and its button contains both icon and text
        !!item.buttonOnly && !item.iconOnly && !!item.icon && (item.buttonText ?? '').length > 0
      );
    default:
      return false;
  }
};
export const getResponsiveSets = overflowConfigs => {
  const groupIndicesMap = new Map(); // Maps responsiveSetId to its indices array
  for (const [i, {
    responsiveSetId
  }] of overflowConfigs.entries()) {
    if (!responsiveSetId) continue;
    if (!groupIndicesMap.get(responsiveSetId)) {
      groupIndicesMap.set(responsiveSetId, []);
    }
    groupIndicesMap.get(responsiveSetId)?.push(i);
  }
  return Array.from(groupIndicesMap.values());
};
//# sourceMappingURL=OverflowUtils.js.map